import React, { useCallback, useEffect, useLayoutEffect } from 'react';

import { estimateCarouselRowHeightPx } from '../domain/carouselLayout';
import { useVideos } from '../providers/VideoContext';
import VideoCarousel from './VideoCarousel';
import FeaturedVideo, { FeaturedVideoInfo } from './FeaturedVideo';
import VideoSearch from './VideoSearch';
import { Language, Video } from '../domain/types';
import LanguageSelector from './LanguageSelector';
import AuthModal from './AuthModal';
import { useTranslate } from '../hooks/useTranslate';

const VideoScreen: React.FC = () => {
  const { 
    data,
    filteredData,
    searchQuery,
    loading,
    login,
    isAuthenticated,
    setIsAuthenticated,
    register,
    accessCodes,
    email,
    showAuthModal,
    setShowAuthModal,
  } = useVideos();

  const params = new URLSearchParams(window.location.search);

  const pk = params.get('video');
  const code = params.get('code');
  const lang = params.get('language');

  const urlLang = window.location.pathname.split('/')[1];

  const logo = 'https://www.alamirefoundation.org/static/images/logo_alpha.png';

  const determineLanguage = () => {
    if (lang && Object.keys(Language).includes(lang)) {
      return lang as Language;
    } else if (urlLang && Object.keys(Language).includes(urlLang)) {
      return urlLang as Language;
    }
    return Language.en;
  };

  const [featuredVid, setFeaturedVid] = React.useState(data?.featured_video);
  const [hasGerman, setHasGerman] = React.useState(false);
  const [hasFrench, setHasFrench] = React.useState(true);
  const [language, setLanguage] = React.useState<Language>(determineLanguage());
  const [ready, setReady] = React.useState(false);

  const [removeOverlay, setRemoveOverlay] = React.useState(false);

  const { updateLanguage, t } = useTranslate();

  const searchActive = searchQuery.trim().length > 0;

  const catalogSectionRef = React.useRef<HTMLDivElement>(null);
  const sharedHeightTrackRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [sharedRowHeight, setSharedRowHeight] = React.useState(() =>
    estimateCarouselRowHeightPx(
      typeof window !== 'undefined' ? window.innerWidth : 0,
    ),
  );

  const firstReferenceSeriesId = React.useMemo(() => {
    const list = filteredData?.docu_series ?? [];
    const withVideos = list.find((s) => s.videos.length > 0);
    return withVideos?.id ?? null;
  }, [filteredData]);

  useLayoutEffect(() => {
    const el = catalogSectionRef.current;
    if (!el) {
      return;
    }

    const applyWidth = (width: number) => {
      setContainerWidth(width);
    };

    applyWidth(el.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      applyWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const trackEl = sharedHeightTrackRef.current;
    const fallback = estimateCarouselRowHeightPx(containerWidth);

    const apply = () => {
      if (trackEl && trackEl.getBoundingClientRect().height > 0) {
        setSharedRowHeight(
          Math.round(trackEl.getBoundingClientRect().height),
        );
      } else {
        setSharedRowHeight(fallback);
      }
    };

    apply();

    if (!trackEl || typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver(apply);
    ro.observe(trackEl);
    return () => ro.disconnect();
  }, [containerWidth, firstReferenceSeriesId]);

  useEffect(() => {
    updateLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const onSetLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  useEffect(() => {
    if (!featuredVid && data) {
      if (pk) {
        const video = data.docu_series
          .map((series) => series.videos)
          .flat()
          .find((vid) => vid.video.pk.toString() === pk);

        if (video) {
          setFeaturedVid(video.video);
          setHasGerman(!!video.video.url_de);
        }
      } else {
        setFeaturedVid(data.featured_video);
        setHasGerman(!!data.featured_video.url_de);
      }
      setReady(true);

      setTimeout(() => {
        console.log('removing overlay');
        setRemoveOverlay(true);
      }, 2000);

    } else if (featuredVid) {
      setReady(true);
      setTimeout(() => {
        setRemoveOverlay(true);
      }, 2000);
    } else {
      setReady(false);
      setRemoveOverlay(false);
    }
  }, [data, featuredVid, pk, removeOverlay]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  const onSetFeatured = (video: Video) => {
    if (featuredVid?.pk === video.pk) {
      return;
    }

    setFeaturedVid(video);
    scrollToTop();
  };

  useEffect(() => {
    setHasFrench(!!featuredVid?.url_fr);
    setHasGerman(!!featuredVid?.url_de);
  }, [featuredVid]);

  useEffect(() => {
    if (!isAuthenticated && code && code === data?.code) {
      setIsAuthenticated(true);
    } else if (!isAuthenticated && code && Object.keys(accessCodes).includes(code)) {
      const expired = accessCodes[code];

      if (!expired) {
        setIsAuthenticated(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, code, isAuthenticated]);

  const onRegister = (address: string, firstName: string, lastName: string, country: string, hasEn: boolean, hasDutch: boolean) => {
    register(address, firstName, lastName, country, hasEn, hasDutch, language);
  }

  if (loading || !data || !featuredVid) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent:'flex-start',
        alignItems: 'flex-start',
        paddingTop: 20,
        paddingLeft: 20,
      }}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <img style={{width: 100, height: 100, marginRight: 20}} src={logo} alt="Alamire logo" />
          <span style={{color: 'white', fontSize: 24}}>ALAMIRE.TV</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{position: 'relative'}}>
      <div className="featured-section">
        <FeaturedVideo video={featuredVid} lang={language} email={email ?? ''} />
        <div className="featured-bar">
          <FeaturedVideoInfo video={featuredVid} lang={language} />
          <LanguageSelector currentLang={language} setCurrentLang={onSetLanguage} hasGerman={hasGerman} hasFrench={hasFrench} />
        </div>
      </div>
      <div className="catalog-section" ref={catalogSectionRef}>
        <div className="catalog-search-row">
          <VideoSearch />
        </div>
        {filteredData?.docu_series.map((series) => {
        const isReferenceTrack =
          firstReferenceSeriesId !== null &&
          series.id === firstReferenceSeriesId;
        return (
          <VideoCarousel
            title={language === 'en' ? series.title_en : series.title_nl}
            videos={series.videos}
            key={series.id}
            setFeatured={v => onSetFeatured(v)}
            language={language}
            searchActive={searchActive}
            emptySearchMessage={t('search.no_results_series')}
            emptyTrackMinHeight={sharedRowHeight}
            sharedHeightTrackRef={
              isReferenceTrack ? sharedHeightTrackRef : undefined
            }
          />
        );
      })}
      </div>
      {showAuthModal && !isAuthenticated && (
        <AuthModal
          login={login}
          register={onRegister}
          language={language}
          onClose={() => setShowAuthModal(false)}
        />
      )}
      <div style={{
        position: 'absolute',
        top: 0,
        width: '100vw',
        height: '100vh',
        display: removeOverlay ? 'none' : 'flex',
        justifyContent:'flex-start',
        alignItems: 'flex-start',
        transition: 'opacity 2s ease',
        backgroundColor: 'black',
        opacity: ready ? 1 : 0,
        paddingTop: 20,
        paddingLeft: 20,
      }}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <img style={{width: 100, height: 100, marginRight: 20}} src={logo} alt="Alamire logo" />
          <span style={{color: 'white', fontSize: 24}}>ALAMIRE.TV</span>
        </div>
      </div>
    </div>
  );
};

export default VideoScreen;